import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { BACKEND_URL } from '../lib/api';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import {
  ArrowDownCircle, AlertCircle, CheckCircle, Copy, Upload, X, Loader2,
  CreditCard, Smartphone, ArrowRight, ArrowLeft, Check, Clock, ExternalLink,
  QrCode, Banknote, FileText, ShieldCheck, AlertTriangle
} from 'lucide-react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function formatCurrency(amount: string | number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(amount));
}

export default function DepositPage() {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [depositRef, setDepositRef] = useState('');
  const [copied, setCopied] = useState('');
  const [utr, setUtr] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const { data: paymentInfo, isLoading: paymentInfoLoading } = useQuery({
    queryKey: ['depositPayment', depositRef],
    queryFn: () => api.get(`/deposits/${depositRef}/payment`).then(r => r.data),
    enabled: step === 2 && !!depositRef,
  });

  const { data: qrData } = useQuery({
    queryKey: ['depositQr', depositRef],
    queryFn: () => api.get(`/deposits/${depositRef}/qr`).then(r => r.data),
    enabled: step === 2 && !!depositRef,
  });

  const { data: depositStatus } = useQuery({
    queryKey: ['depositStatus', depositRef],
    queryFn: () => api.get(`/deposits/${depositRef}/status`).then(r => r.data),
    enabled: step === 3 && !!depositRef,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'APPROVED' || status === 'REJECTED' || status === 'CANCELLED') return false;
      return 5000;
    },
  });

  const createDepositMutation = useMutation({
    mutationFn: (data: { amount: number; paymentMethod: string }) => api.post('/deposits', data),
    onSuccess: (res) => {
      setDepositRef(res.data.depositRef);
      setStep(2);
      setError('');
    },
    onError: (err: any) => { setError(err.response?.data?.message || 'Failed to create deposit request'); },
  });

  const submitProofMutation = useMutation({
    mutationFn: (data: { transactionId?: string; paymentScreenshot?: string }) =>
      api.post(`/deposits/${depositRef}/proof`, data),
    onSuccess: (res) => {
      setStep(3);
      setError('');
      queryClient.invalidateQueries({ queryKey: ['depositStatus', depositRef] });
    },
    onError: (err: any) => { setError(err.response?.data?.message || 'Failed to submit proof'); },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/upload/payment-proof', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: (res) => { setScreenshot(res.data.url); setUploading(false); },
    onError: () => { setUploading(false); setError('Failed to upload file'); },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/deposits/${depositRef}/cancel`),
    onSuccess: () => { setStep(1); setDepositRef(''); setAmount(''); setUtr(''); setScreenshot(''); setError(''); },
  });

  const handleCreateDeposit = () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter a valid amount'); return; }
    setError('');
    createDepositMutation.mutate({ amount: amt, paymentMethod: 'upi' });
  };

  const handleSubmitProof = () => {
    if (!utr.trim() && !screenshot) {
      setError('Please provide a Transaction ID/UTR or upload a payment screenshot');
      return;
    }
    if (utr.trim() && (utr.trim().length < 6 || utr.trim().length > 50)) {
      setError('Transaction ID/UTR must be between 6 and 50 characters');
      return;
    }
    setError('');
    submitProofMutation.mutate({
      transactionId: utr.trim() || undefined,
      paymentScreenshot: screenshot || undefined,
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('File size must be less than 5MB'); return; }
    setUploading(true);
    setError('');
    uploadMutation.mutate(file);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handlePayWithApp = () => {
    if (paymentInfo?.deepLink) {
      window.location.href = paymentInfo.deepLink;
    }
  };

  const resetFlow = () => {
    setStep(1);
    setDepositRef('');
    setAmount('');
    setUtr('');
    setScreenshot('');
    setError('');
    setSuccess(false);
  };

  return (
    <div className="max-w-lg mx-auto space-y-6 animate-fade-in">
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">Deposit Funds</h1>
        <p className="text-slate-400 mt-1">Add funds to your wallet via UPI</p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-colors ${
              step >= s ? 'bg-primary-500 text-white' : 'bg-white/[0.06] text-slate-500'
            }`}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < 3 && <div className={`flex-1 h-0.5 rounded transition-colors ${step > s ? 'bg-primary-500' : 'bg-white/[0.06]'}`} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {step === 1 && (
        <Card>
          <CardHeader><h2 className="text-base font-semibold text-white flex items-center gap-2"><Banknote className="w-4 h-4 text-primary-400" />Enter Amount</h2></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Deposit Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-slate-400">₹</span>
                  <input type="number" min="1" step="any" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl text-2xl font-bold text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
              </div>
              <Button onClick={handleCreateDeposit} isLoading={createDepositMutation.isPending} className="w-full" disabled={!amount || parseFloat(amount) <= 0}>
                Continue to Payment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {paymentInfoLoading ? (
            <Card><CardContent className="py-12 text-center"><Loader2 className="w-6 h-6 text-primary-400 animate-spin mx-auto" /><p className="text-sm text-slate-400 mt-2">Loading payment info...</p></CardContent></Card>
          ) : (
            <>
              <div className="p-4 rounded-xl border border-primary-500/20 bg-primary-500/5">
                <div className="text-center mb-4">
                  <p className="text-sm text-slate-400">Pay</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(paymentInfo?.amount || amount)}</p>
                </div>

                <div className="space-y-3">
                  <div className="p-3 bg-white/[0.06] rounded-lg flex items-center justify-between">
                    <div><p className="text-xs text-slate-400">Deposit Reference</p><p className="text-sm font-mono font-semibold text-primary-400">{depositRef}</p></div>
                    <button onClick={() => handleCopy(depositRef, 'ref')} className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white">
                      {copied === 'ref' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {isMobile && paymentInfo?.upiIntentEnabled !== false && (
                <Button onClick={handlePayWithApp} className="w-full" size="lg">
                  <Smartphone className="w-5 h-5 mr-2" />Pay with UPI App
                </Button>
              )}

              {paymentInfo?.qrEnabled !== false && qrData?.qr && (
                <Card>
                  <CardHeader><h2 className="text-base font-semibold text-white flex items-center gap-2"><QrCode className="w-4 h-4 text-primary-400" />Scan QR Code</h2></CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center gap-4">
                      <div className="bg-white p-4 rounded-2xl">
                        <img src={qrData.qr} alt="UPI QR Code" className="w-56 h-56 object-contain" />
                      </div>
                      <p className="text-xs text-slate-400 text-center">Scan this QR code using any UPI app</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader><h2 className="text-base font-semibold text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary-400" />Payment Details</h2></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-white/[0.04] rounded-lg">
                      <span className="text-xs text-slate-400">Payee</span>
                      <span className="text-sm font-medium text-white">{paymentInfo?.payeeName}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/[0.04] rounded-lg">
                      <div><span className="text-xs text-slate-400 block">UPI ID</span><span className="text-sm font-mono font-medium text-white">{paymentInfo?.upiId}</span></div>
                      <button onClick={() => handleCopy(paymentInfo?.upiId, 'upi')} className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white">
                        {copied === 'upi' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/[0.04] rounded-lg">
                      <span className="text-xs text-slate-400">Amount</span>
                      <span className="text-sm font-bold text-white">{formatCurrency(paymentInfo?.amount || amount)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/[0.04] rounded-lg">
                      <span className="text-xs text-slate-400">Reference</span>
                      <span className="text-sm font-mono text-primary-400">{depositRef}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
                <p className="text-xs text-amber-400 flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4" />After completing payment, return here and submit your proof below.</p>
              </div>

              <Card>
                <CardHeader><h2 className="text-base font-semibold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-primary-400" />Payment Proof</h2></CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Transaction ID / UTR</label>
                      <input type="text" value={utr} onChange={(e) => setUtr(e.target.value)}
                        placeholder="Enter UTR / Transaction ID (optional if screenshot provided)"
                        className="w-full px-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">OR Upload Screenshot</label>
                      {screenshot ? (
                        <div className="relative inline-block">
                          <img src={screenshot.startsWith('/') ? `${BACKEND_URL}${screenshot}` : screenshot} alt="Proof" className="max-w-full max-h-48 rounded-xl border border-white/10 object-contain" />
                          <button onClick={() => setScreenshot('')} className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full hover:bg-red-500">
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-primary-400/50 transition-colors">
                          {uploading ? <Loader2 className="w-8 h-8 text-primary-400 animate-spin" /> : <Upload className="w-8 h-8 text-slate-400" />}
                          <span className="text-sm text-slate-400">{uploading ? 'Uploading...' : 'Click to upload screenshot'}</span>
                          <span className="text-xs text-slate-500">JPG, PNG, WebP or PDF (max 5MB)</span>
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileSelect} disabled={uploading} />
                        </label>
                      )}
                    </div>

                    {!utr.trim() && !screenshot && (
                      <p className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Provide at least one: Transaction ID or Screenshot</p>
                    )}

                    <Button onClick={handleSubmitProof} isLoading={submitProofMutation.isPending} className="w-full"
                      disabled={(!utr.trim() && !screenshot) || uploading}>
                      <CheckCircle className="w-4 h-4 mr-2" />Submit Deposit Request
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Button variant="secondary" onClick={() => cancelMutation.mutate()} className="w-full" size="sm" disabled={cancelMutation.isPending}>
                Cancel Deposit
              </Button>
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Card>
            <CardContent className="py-8 text-center">
              {depositStatus?.status === 'APPROVED' ? (
                <div className="space-y-3">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Deposit Approved!</h3>
                  <p className="text-sm text-slate-400">Your wallet has been credited with {formatCurrency(depositStatus.amount)}</p>
                  <Button onClick={resetFlow} className="mt-4">Make Another Deposit</Button>
                </div>
              ) : depositStatus?.status === 'REJECTED' ? (
                <div className="space-y-3">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Deposit Rejected</h3>
                  <p className="text-sm text-slate-400">{depositStatus.adminNote || 'Your deposit was not approved'}</p>
                  <Button onClick={resetFlow} className="mt-4">Try Again</Button>
                </div>
              ) : depositStatus?.status === 'CANCELLED' ? (
                <div className="space-y-3">
                  <div className="w-16 h-16 rounded-full bg-slate-500/20 flex items-center justify-center mx-auto">
                    <X className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Deposit Cancelled</h3>
                  <Button onClick={resetFlow} className="mt-4">Make Another Deposit</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto">
                    <Clock className="w-8 h-8 text-amber-400 animate-pulse" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Payment Proof Submitted</h3>
                  <p className="text-sm text-slate-400">Your deposit is under review. This may take a few minutes.</p>

                  <div className="mt-4 space-y-2 text-left max-w-xs mx-auto">
                    <div className="flex justify-between p-2 bg-white/[0.04] rounded-lg text-sm">
                      <span className="text-slate-400">Reference</span>
                      <span className="font-mono text-primary-400">{depositRef}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-white/[0.04] rounded-lg text-sm">
                      <span className="text-slate-400">Amount</span>
                      <span className="font-medium text-white">{formatCurrency(depositStatus?.amount || amount)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-white/[0.04] rounded-lg text-sm">
                      <span className="text-slate-400">Status</span>
                      <span className="text-amber-400">{depositStatus?.status?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
